import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { I18nService } from 'i18n';
import { CommunityModerationService } from '../../services/community-moderation.service';
import {
  CommunityModerationAction,
  CommunityModerationUserSummary,
  moderationUserName
} from '../../models/community-moderation.models';
import { communityErrorKey } from '../../models/community.models';
import { CommunityReasonModalComponent } from '../community-reason-modal/community-reason-modal.component';

/**
 * A moderator's view of one member (`GET /community/moderation/users/:id`):
 * active sanctions (liftable from here), the last warnings and how much they
 * wrote. Opens as a dialog from the protocol rows and from an author's name.
 */
@Component({
  selector: 'app-community-member-summary',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslocoModule, CommunityReasonModalComponent],
  template: `
    <ng-container *transloco="let t">
      <div class="modal fade show d-block" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-lg modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                {{ t('community.moderation.summary.title') }}
                @if (summary(); as s) { — {{ userName(s.user) }} <small class="text-muted">#{{ s.user.id }}</small> }
              </h5>
              <button type="button" class="btn-close" (click)="closed.emit()"></button>
            </div>
            <div class="modal-body">
              @if (loading()) {
                <div class="text-center py-3"><div class="spinner-border" role="status"></div></div>
              } @else if (error(); as key) {
                <div class="alert alert-warning">{{ t(key) }}</div>
              } @else if (summary(); as s) {
                <div class="d-flex flex-wrap gap-3 mb-3 small text-muted">
                  @if (s.user.email) { <span>{{ s.user.email }}</span> }
                  <span>{{ t('community.moderation.summary.threads', { count: s.thread_count }) }}</span>
                  <span>{{ t('community.moderation.summary.posts', { count: s.post_count }) }}</span>
                  <a [routerLink]="memberLink(s.user.id)" (click)="closed.emit()">{{ t('community.member.view_profile') }}</a>
                </div>

                <h6>{{ t('community.moderation.summary.active') }}</h6>
                @if (s.active_sanctions.length === 0) {
                  <p class="text-muted small">{{ t('community.moderation.summary.no_sanctions') }}</p>
                } @else {
                  <ul class="list-group mb-3">
                    @for (a of s.active_sanctions; track a.id) {
                      <li class="list-group-item d-flex flex-wrap align-items-center gap-2">
                        <span class="badge" [class]="'badge ' + kindClass(a.kind)">{{ t('community.moderation.kind.' + a.kind) }}</span>
                        <span class="flex-grow-1">
                          {{ a.scope_name || t('community.moderation.scope_network') }}
                          <span class="text-muted small">· {{ a.created_at | date:'medium' }}</span>
                          @if (a.until) { <span class="text-muted small">· {{ t('community.moderation.until_label') }} {{ a.until | date:'medium' }}</span> }
                          <div class="small">{{ a.reason }}</div>
                        </span>
                        <button type="button" class="btn btn-sm btn-outline-success" [disabled]="busy()" (click)="liftTarget.set(a)">
                          {{ t('community.moderation.lift') }}
                        </button>
                      </li>
                    }
                  </ul>
                }

                <h6>{{ t('community.moderation.summary.warnings') }}</h6>
                @if (s.warnings.length === 0) {
                  <p class="text-muted small mb-0">{{ t('community.moderation.summary.no_warnings') }}</p>
                } @else {
                  <ul class="small mb-0">
                    @for (w of s.warnings; track w.id) {
                      <li>
                        <span class="text-muted">{{ w.created_at | date:'medium' }}</span>
                        @if (w.scope_name) { <span class="text-muted"> · {{ w.scope_name }}</span> }
                        · {{ userName(w.actor) }} — {{ w.reason }}
                      </li>
                    }
                  </ul>
                }
              }
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-outline-secondary" (click)="closed.emit()">{{ t('community.history.hide') }}</button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>

      @if (liftTarget(); as target) {
        <app-community-reason-modal titleKey="community.moderation.lift_title"
                                    [titleParams]="{ kind: t('community.moderation.kind.' + target.kind), name: userName(target.user) }"
                                    confirmKey="community.moderation.lift"
                                    confirmClass="btn-success"
                                    [busy]="busy()"
                                    [error]="liftError()"
                                    (confirmed)="lift(target, $event)"
                                    (cancelled)="liftTarget.set(null)" />
      }
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityMemberSummaryComponent implements OnInit {
  @Input({ required: true }) userId!: number;
  @Output() closed = new EventEmitter<void>();
  /** A sanction was lifted: hosts reload whatever list they show. */
  @Output() changed = new EventEmitter<void>();

  summary = signal<CommunityModerationUserSummary | null>(null);
  loading = signal(false);
  error = signal<string | null>(null);
  busy = signal(false);
  liftTarget = signal<CommunityModerationAction | null>(null);
  liftError = signal<string | null>(null);

  readonly userName = moderationUserName;

  constructor(private moderation: CommunityModerationService, private i18n: I18nService) {}

  ngOnInit(): void {
    this.load();
  }

  memberLink(userId: number): string {
    return `/${this.i18n.getLanguage()}/community/u/${userId}`;
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.moderation.getUserSummary(this.userId).subscribe({
      next: res => {
        this.summary.set(res);
        this.loading.set(false);
      },
      error: err => {
        this.loading.set(false);
        this.error.set(err?.status === 404 ? 'community.moderation.summary.not_found' : communityErrorKey(err));
      }
    });
  }

  lift(action: CommunityModerationAction, reason: string): void {
    this.busy.set(true);
    this.liftError.set(null);
    this.moderation.liftAction(action.id, reason).subscribe({
      next: () => {
        this.busy.set(false);
        this.liftTarget.set(null);
        this.changed.emit();
        this.load();
      },
      error: err => {
        this.busy.set(false);
        this.liftError.set(communityErrorKey(err));
      }
    });
  }

  kindClass(kind: string): string {
    switch (kind) {
      case 'ban': return 'bg-danger';
      case 'hide': return 'bg-dark';
      case 'mute': return 'bg-warning text-dark';
      case 'warn': return 'bg-info text-dark';
      default: return 'bg-success';
    }
  }
}
